/** This provider overwrites the I18nProvider of LinguiJS by adding logic to
 * detect the local language of the user. As we don't have an option to switch
 * languages, we detect the locale in getLocale and use that.
 */
import * as React from 'react';
import { I18nProvider } from '@lingui/react';
import { i18n, type Messages } from '@lingui/core';
import { userSession } from 'state/session';

/** Loads common messages for a specific locale. Only wraps the I/O operation. */
const loadCommonMessages = async (locale: string): Promise<Messages | null> => {
  try {
    const { messages } = await import(`./locales/${locale}.mjs`);
    return messages;
  } catch {
    return null;
  }
};

/** Loads branded messages for a specific locale and brand. Only wraps the I/O operation. */
const loadBrandedMessages = async (locale: string, brand: string | undefined): Promise<Messages | null> => {
  if (!brand) {
    return null;
  }
  try {
    const { messages } = await import(`./locales/${locale}-${brand}.mjs`);
    return messages;
  } catch {
    return null;
  }
};

export const I18nContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale, brand } = userSession().useState();
  const [i18nLoaded, setI18nLoaded] = React.useState(false);

  React.useEffect(() => {
    if (locale) {
      (async () => {
        // Common and branded catalogs are independent files — fetch them together.
        // Each of these awaits used to run serially, adding a full network round trip
        // to the startup critical path (nothing renders until i18n resolves).
        const [commonMessages, brandedMessages] = await Promise.all([
          loadCommonMessages(locale),
          loadBrandedMessages(locale, brand),
        ]);

        if (!commonMessages) {
          // Locale failed to load, fall back to English
          console.error(`Failed to load locale ${locale}, falling back to English`);
          const [englishCommonMessages, englishBrandedMessages] = await Promise.all([
            loadCommonMessages('en'),
            loadBrandedMessages('en', brand),
          ]);

          if (!englishCommonMessages) {
            throw new Error('Your locale failed to load. Please make sure you are online and reload the page.');
          }

          const messages = englishBrandedMessages
            ? { ...englishCommonMessages, ...englishBrandedMessages }
            : englishCommonMessages;

          i18n.loadAndActivate({ locale, messages });
          setI18nLoaded(true);
          return;
        }

        if (brandedMessages) {
          // Brand-specific messages override common messages
          const messages = { ...commonMessages, ...brandedMessages };
          i18n.loadAndActivate({ locale, messages });
          setI18nLoaded(true);
          return;
        }

        // Branded catalog doesn't exist, try English branded as fallback
        const englishBrandedMessages = await loadBrandedMessages('en', brand);

        const messages = englishBrandedMessages ? { ...commonMessages, ...englishBrandedMessages } : commonMessages;

        i18n.loadAndActivate({ locale, messages });
        setI18nLoaded(true);
      })();
    }
  }, [locale, brand]);

  if (!locale || !i18nLoaded) {
    return null;
  }
  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};
